import { C, Section, Badge, Banner, thStyle, tdStyle, theadRowStyle } from "./ui.jsx";
import { SETTING_LABELS } from "../constants.js";

const isOn = (v) => v === true || v === "true";

function BoolBadge({ value }) {
  return isOn(value)
    ? <Badge label="On" bg="#E8F5E9" color={C.success} />
    : <Badge label="Off" bg="#F4F2EE" color={C.sub} />;
}

/* Read-only summaries for settings, routing, and templates. No mutation controls. */
export default function ControlSummary({ settings, routing, templates }) {
  const boolKeys = [
    "birthday.automation_enabled", "birthday.dry_run_enabled", "birthday.n8n_integration_enabled",
    "birthday.email_fallback_enabled", "birthday.test_mode_enabled",
  ];
  const sot = settings["birthday.source_of_truth"];
  const defTpl = settings["birthday.default_template_id"];

  const active = (routing || []).filter((r) => r.is_active);
  const hasDefault = active.some((r) => r.mode === "default");
  const hasTest = active.some((r) => r.mode === "test");
  const testMode = isOn(settings["birthday.test_mode_enabled"]);
  const failClosedWarning = testMode ? !hasTest : !hasDefault;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 20 }}>
      <Section title="Settings & kill switches" sub="Read-only. Changes are Admin-only and made elsewhere.">
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", rowGap: 8, columnGap: 12, alignItems: "center" }}>
          {boolKeys.map((k) => (
            <Row key={k} label={SETTING_LABELS[k]}><BoolBadge value={settings[k]} /></Row>
          ))}
          <Row label={SETTING_LABELS["birthday.source_of_truth"]}>
            <Badge label={String(sot || "—").replace(/"/g, "")} bg="#E3F2FD" color="#1565C0" />
          </Row>
          <Row label={SETTING_LABELS["birthday.default_template_id"]}>
            <span style={{ fontSize: 12, color: C.sub, fontFamily: "ui-monospace,Menlo,monospace" }}>
              {defTpl ? String(defTpl).replace(/"/g, "").slice(0, 12) + "…" : "—"}
            </span>
          </Row>
        </div>
      </Section>

      <Section title="Routing" sub="Recipient resolution: test → department → default → fail-closed.">
        {failClosedWarning && (
          <div style={{ marginBottom: 10 }}>
            <Banner tone="warn">
              {testMode
                ? "Test mode is on but no active test route exists — test sends would fail closed."
                : "No active default route — non-test sends would fail closed."}
            </Banner>
          </div>
        )}
        {active.length === 0
          ? <div style={{ fontSize: 12.5, color: C.muted }}>No active routing rules.</div>
          : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={theadRowStyle}><th style={thStyle}>Name</th><th style={thStyle}>Mode</th><th style={thStyle}>Recipients</th></tr></thead>
              <tbody>
                {active.map((r) => (
                  <tr key={r.id}>
                    <td style={tdStyle}>{r.name}</td>
                    <td style={tdStyle}><Badge label={r.mode} bg="#F4F2EE" color={C.sub} /></td>
                    <td style={{ ...tdStyle, color: C.sub }}>{(r.recipient_emails || []).join(", ") || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </Section>

      <Section title="Templates" sub="v1 JSON templates. Read-only." style={{ gridColumn: "1 / -1" }}>
        {(!templates || templates.length === 0)
          ? <div style={{ fontSize: 12.5, color: C.muted }}>No templates.</div>
          : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={theadRowStyle}><th style={thStyle}>Name</th><th style={thStyle}>Version</th><th style={thStyle}>Active</th><th style={thStyle}>Default</th></tr></thead>
              <tbody>
                {templates.map((t) => (
                  <tr key={t.id}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{t.name}</td>
                    <td style={tdStyle}>v{t.version}</td>
                    <td style={tdStyle}>{t.is_active ? <Badge label="Active" bg="#E8F5E9" color={C.success} /> : <Badge label="Inactive" />}</td>
                    <td style={tdStyle}>{t.is_default ? <Badge label="Default" bg="#FFF0ED" color={C.accent} /> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </Section>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <>
      <div style={{ fontSize: 12.5, color: C.sub }}>{label}</div>
      <div style={{ textAlign: "right" }}>{children}</div>
    </>
  );
}
